import { useQuery } from '@tanstack/react-query';

import { MouseEvent, useMemo, useState } from 'react';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';
import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	Menu,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Tooltip,
	Typography,
} from '@mui/material';

import { useApiClient, useToastService } from '@@common';

import { getValidationErrorMessage, parseValidationError } from '../../api/ApiValidationUtils';
import { ContextClient, ContextModel, ContextScopeModel } from '../../api/api';
import { ScopeDialog } from '../Scopes';
import { contextDesignerQueryKeys } from './ContextDesignerQueryKeys';

type DomainScopesViewProps = {
	contextId: string;
};

type SortBy = 'name' | 'description';
type SortDirection = 'asc' | 'desc';

export default function DomainScopesView({ contextId }: DomainScopesViewProps) {
	const client = useApiClient(ContextClient);
	const toastService = useToastService();

	const [openCreateDialog, setOpenCreateDialog] = useState(false);
	const [openEditDialog, setOpenEditDialog] = useState(false);
	const [selectedScope, setSelectedScope] = useState<ContextScopeModel | null>(null);
	const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
	const [actionsScope, setActionsScope] = useState<ContextScopeModel | null>(null);
	const [sortBy, setSortBy] = useState<SortBy>('name');
	const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

	const { data, isLoading, refetch } = useQuery<ContextModel>({
		queryKey: contextDesignerQueryKeys.context(contextId),
		queryFn: async () => {
			if (!contextId) throw new Error('Context ID is required');
			return client.getContextById(contextId);
		},
		enabled: !!contextId,
	});

	const scopes = useMemo(() => {
		const sortMultiplier = sortDirection === 'asc' ? 1 : -1;

		return [...(data?.scopes ?? [])].sort((left, right) => {
			const result = (left[sortBy] ?? '').localeCompare(right[sortBy] ?? '', undefined, { sensitivity: 'base' });
			return result === 0 ? left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }) : result * sortMultiplier;
		});
	}, [data?.scopes, sortBy, sortDirection]);

	const handleSort = (nextSortBy: SortBy) => {
		if (sortBy === nextSortBy) {
			setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
			return;
		}

		setSortBy(nextSortBy);
		setSortDirection('asc');
	};

	const handleOpenActions = (event: MouseEvent<HTMLElement>, scope: ContextScopeModel) => {
		setActionsAnchorEl(event.currentTarget);
		setActionsScope(scope);
	};

	const handleCloseActions = () => {
		setActionsAnchorEl(null);
		setActionsScope(null);
	};

	const handleDeleteScope = async (scope: ContextScopeModel) => {
		if (!contextId) return;

		try {
			toastService.info(`Deleting scope: ${scope.name}...`);
			await client.removeContextScope(contextId, scope.id);
			toastService.success(`Scope "${scope.name}" deleted successfully.`);
			refetch();
		} catch (error: any) {
			console.error('Error deleting scope:', error);

			// Handle validation errors for delete operations
			const validationError = parseValidationError(error);
			if (validationError) {
				const userMessage = getValidationErrorMessage(validationError);
				toastService.error(`Failed to delete scope "${scope.name}": ${userMessage}`);
			} else {
				const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
				toastService.error(`Failed to delete scope "${scope.name}": ${errorMessage}`);
			}
		}
	};

	const handleEditScope = (scope: ContextScopeModel) => {
		setSelectedScope(scope);
		setOpenEditDialog(true);
	};

	const handleCopyId = (scope: ContextScopeModel) => {
		navigator.clipboard.writeText(scope.id);
		toastService.success(`ID copied to clipboard`);
	};

	return (
		<Stack direction="column" sx={{ flex: 1, minHeight: 0, overflow: 'hidden', bgcolor: 'background.default' }}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{ px: 2, py: 1.5, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}
			>
				<Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
					<SecurityIcon color="action" />
					<Box sx={{ minWidth: 0 }}>
						<Typography variant="subtitle1" fontWeight={700}>
							Authorization Scopes
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{scopes.length} configured for this context
						</Typography>
					</Box>
				</Stack>
				<Stack direction="row" alignItems="center" spacing={0.5}>
					<Button variant="contained" size="small" startIcon={<AddCircleIcon />} onClick={() => setOpenCreateDialog(true)}>
						Create
					</Button>
					<Tooltip title="Refresh authorization scopes">
						<span>
							<IconButton size="small" onClick={() => refetch()} disabled={isLoading}>
								<RefreshIcon />
							</IconButton>
						</span>
					</Tooltip>
				</Stack>
			</Stack>

			{isLoading ? (
				<Stack alignItems="center" justifyContent="center" spacing={2} sx={{ flex: 1, p: 3 }}>
					<CircularProgress size={28} />
					<Typography variant="body2" color="text.secondary">
						Loading authorization scopes
					</Typography>
				</Stack>
			) : scopes.length === 0 ? (
				<Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ flex: 1, p: 3, textAlign: 'center' }}>
					<SecurityIcon color="disabled" sx={{ fontSize: 48 }} />
					<Typography variant="subtitle2">No authorization scopes configured</Typography>
					<Button variant="outlined" size="small" startIcon={<AddCircleIcon />} onClick={() => setOpenCreateDialog(true)}>
						Create scope
					</Button>
				</Stack>
			) : (
				<TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: 'background.paper' }}>
					<Table stickyHeader size="small" aria-label="Authorization scopes">
						<TableHead>
							<TableRow>
								<TableCell>
									<TableSortLabel active={sortBy === 'name'} direction={sortBy === 'name' ? sortDirection : 'asc'} onClick={() => handleSort('name')}>
										Name
									</TableSortLabel>
								</TableCell>
								<TableCell>
									<TableSortLabel
										active={sortBy === 'description'}
										direction={sortBy === 'description' ? sortDirection : 'asc'}
										onClick={() => handleSort('description')}
									>
										Description
									</TableSortLabel>
								</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{scopes.map((scope) => (
								<TableRow key={scope.id} hover>
									<TableCell>
										<Typography variant="body2" fontWeight={600}>
											{scope.name}
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="body2" color={scope.description ? 'text.primary' : 'text.secondary'}>
											{scope.description || '-'}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Tooltip title="Scope actions">
											<IconButton size="small" onClick={(event) => handleOpenActions(event, scope)}>
												<MoreVertIcon />
											</IconButton>
										</Tooltip>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<Menu anchorEl={actionsAnchorEl} open={Boolean(actionsAnchorEl)} onClose={handleCloseActions}>
				<MenuItem
					onClick={() => {
						if (actionsScope) {
							handleCopyId(actionsScope);
						}
						handleCloseActions();
					}}
				>
					<ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
					Copy ID
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (actionsScope) {
							handleEditScope(actionsScope);
						}
						handleCloseActions();
					}}
				>
					<EditIcon fontSize="small" sx={{ mr: 1 }} />
					Edit Scope
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (actionsScope) {
							handleDeleteScope(actionsScope);
						}
						handleCloseActions();
					}}
				>
					<DeleteIcon fontSize="small" sx={{ mr: 1 }} />
					Delete Scope
				</MenuItem>
			</Menu>

			<ScopeDialog open={openCreateDialog} setOpen={setOpenCreateDialog} contextId={contextId} onSuccess={() => refetch()} />
			<ScopeDialog
				open={openEditDialog}
				setOpen={setOpenEditDialog}
				contextId={contextId}
				scope={selectedScope || undefined}
				onSuccess={() => {
					refetch();
					setSelectedScope(null);
				}}
			/>
		</Stack>
	);
}
